import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Badge,
  Link,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
} from '@fluentui/react-components';
import { CalendarLtr20Regular, Open16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { SpaceWithAvailability } from '@/store/models/spaces';
import type { EventSpaceSelection } from '@/store/models/events';
import { SpaceRow } from './SpaceRow';
import { CapacitySummary } from './CapacitySummary';
import { useSelectedSpacesEditorStyles } from './SelectedSpacesEditor.styles';

export interface SelectedSpacesEditorProps {
  spaces: EventSpaceSelection[];
  availableSpaces: SpaceWithAvailability[];
  attendeeCount: number;
  onChange: (spaces: EventSpaceSelection[]) => void;
  disabled?: boolean;
  locationLocked?: boolean;
  isLoading?: boolean;
  onViewCalendar?: () => void;
  hasUnsavedChanges?: boolean;
}

export const SelectedSpacesEditor = ({
  spaces,
  availableSpaces,
  attendeeCount,
  onChange,
  disabled,
  locationLocked = false,
  isLoading,
  onViewCalendar,
  hasUnsavedChanges,
}: SelectedSpacesEditorProps) => {
  const styles = useSelectedSpacesEditorStyles();
  const { t } = useTranslation('planner');

  const selectedIds = useMemo(
    () => new Set(spaces.map((s) => s.spaceId as string).filter(Boolean)),
    [spaces]
  );

  const hasUnselectedSpaces = useMemo(
    () => availableSpaces.some((s) => !selectedIds.has(s.spaceId as string)),
    [availableSpaces, selectedIds]
  );

  const totalCapacity = useMemo(
    () =>
      spaces.reduce((sum, s) => {
        if (!s.spaceId) return sum;
        const space = availableSpaces.find((a) => a.spaceId === s.spaceId);
        return sum + (space?.roomCapacity ?? 0);
      }, 0),
    [spaces, availableSpaces]
  );

  const handleSpaceChange = useCallback(
    (index: number, spaceId: string, spaceName: string) => {
      const next = spaces.map((s, i) =>
        i === index
          ? { ...s, spaceId: spaceId as EventSpaceSelection['spaceId'], spaceName, selectedSetupCodes: [], setupCapacity: 0 }
          : s
      );
      onChange(next);
    },
    [spaces, onChange]
  );

  const handleSetupChange = useCallback(
    (index: number, codes: string[], setupCapacity: number) => {
      const next = spaces.map((s, i) =>
        i === index ? { ...s, selectedSetupCodes: codes, setupCapacity } : s
      );
      onChange(next);
    },
    [spaces, onChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const next = spaces.filter((_, i) => i !== index);
      onChange(next);
    },
    [spaces, onChange]
  );

  const hasEmptyRow = useMemo(
    () => spaces.some((s) => !s.spaceId),
    [spaces]
  );

  const hasIncompleteRow = useMemo(
    () => spaces.some((s) => s.spaceId && s.selectedSetupCodes.length === 0),
    [spaces]
  );

  const handleAdd = useCallback(() => {
    const empty: EventSpaceSelection = {
      spaceId: '' as EventSpaceSelection['spaceId'],
      spaceName: '',
      selectedSetupCodes: [],
      setupCapacity: 0,
    };
    onChange([...spaces, empty]);
  }, [spaces, onChange]);

  const [showCalendarConfirm, setShowCalendarConfirm] = useState(false);

  return (
    <div className={styles.root} data-testid="selected-spaces-editor">
      <div className={styles.header}>
        <span>{t('reviewPanel.selectedSpaces')}</span>
        <Badge appearance="filled" color="informative" className={styles.badge}>
          {t('reviewPanel.selectedSpacesCount', { count: spaces.filter((s) => s.spaceId).length })}
        </Badge>
      </div>

      <div className={styles.card}>
        <div className={styles.columnHeaders}>
          <span>{t('reviewPanel.spaceColumnHeader')}</span>
          <span>{t('reviewPanel.roomSetupColumnHeader')}</span>
          <span className={styles.columnHeaderSpacer} />
        </div>

        <div className={styles.spaceList} aria-live="polite">
          {spaces.map((selection, index) => {
            const rowAvailableSpaces = availableSpaces.filter(
              (s) => s.spaceId === selection.spaceId || !selectedIds.has(s.spaceId as string)
            );
            return (
              <SpaceRow
                key={selection.spaceId || `empty-${index}`}
                selection={selection}
                availableSpaces={rowAvailableSpaces}
                onSpaceChange={(id, name) => handleSpaceChange(index, id, name)}
                onSetupChange={(codes, cap) => handleSetupChange(index, codes, cap)}
                onRemove={() => handleRemove(index)}
                canRemove={spaces.length > 1}
                disabled={disabled}
                locationLocked={locationLocked}
                focusOnMount={!selection.spaceId && index === spaces.length - 1 && spaces.length > 1}
                isLoading={isLoading}
              />
            );
          })}
        </div>

        <div className={styles.addButtonRow}>
          <Button
            appearance="outline"
            onClick={handleAdd}
            disabled={disabled || locationLocked || hasEmptyRow || hasIncompleteRow || (availableSpaces.length > 0 && !hasUnselectedSpaces)}
            className={styles.addButton}
            data-testid="add-space-button"
          >
            {t('reviewPanel.addAnotherSpace')}
          </Button>
        </div>
      </div>

      <CapacitySummary
        spaceCount={spaces.filter((s) => s.spaceId).length}
        totalCapacity={totalCapacity}
        attendeeCount={attendeeCount}
      />

      <Link
        className={styles.calendarLink}
        data-testid="calendar-link"
        onClick={(e) => {
          e.preventDefault();
          if (hasUnsavedChanges) {
            setShowCalendarConfirm(true);
          } else {
            onViewCalendar?.();
          }
        }}
      >
        <CalendarLtr20Regular />
        {t('reviewPanel.viewInCalendar')}
        <Open16Regular />
      </Link>

      <Dialog open={showCalendarConfirm} onOpenChange={(_e, data) => setShowCalendarConfirm(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('reviewPanel.unsavedChangesTitle')}</DialogTitle>
            <DialogContent>{t('reviewPanel.unsavedChangesMessage')}</DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('reviewPanel.cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={() => {
                  setShowCalendarConfirm(false);
                  onViewCalendar?.();
                }}
              >
                {t('reviewPanel.continueToCalendar')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

SelectedSpacesEditor.displayName = 'SelectedSpacesEditor';
